<template>
  <v-navigation-drawer
    id="right-drawer"
    temporary
    :stateless="$vuetify.breakpoint.mdAndUp"
    fixed
    v-model="drawer"
    right
    touchless
    :cancelCallback="closeDrawer"
    style="top: 48px;"
    height="calc(100% - 48px)"
    :width="420"
    :style="{overflow: 'auto'}"
    :aria-label="$t(title)"
    :title="$t(title)"
    role="complementary"
  >
    <v-toolbar flat color="primary" style="position: sticky; top: 0; z-index: 30" role="navigation" :aria-label="$t(title)" :title="$t(title)">
      <v-list>
        <v-list-tile>
          <v-btn flat icon :id="closeId" :aria-label="closeId" @click="cancelClicked" class="back-button" :disabled="backButtonDisabled">
            <v-icon :style="textColor">arrow_back</v-icon>
          </v-btn>
          <v-list-tile-title class="title">
            <span :style="textColor">{{$t(title)}}</span>
          </v-list-tile-title>
        </v-list-tile>
      </v-list>
    </v-toolbar>
    <intervention-form ref="intForm" v-if="component==='CREATE_INTERVENTION'"
                       :cancel="cancelClicked"/>
    <close-intervention-form ref="closeIntForm"
                             v-if="component==='CLOSE_INTERVENTION'"
                             :cancel="cancelClicked"/>
    <exemption-form ref="exemptionForm" v-if="component==='CREATE_EXEMPTION'"/>
    <chart-filter-form ref="chartFilterForm" v-if="component==='CREATE_CHART_FILTER' && drawer"/>
    <alert-filter-body ref="alertFilterForm"  v-if="component==='ALERT_FILTER' && drawer"></alert-filter-body>
    <v-container v-if="component==='INTERACTION_FILTER'">
      <interactions-filter/>
    </v-container>
    <v-container v-if="component==='STUDENT_FILTER'">
      <student-filter-container/>
    </v-container>
    <v-container v-if="component==='FEED_FILTER'">
      <feed-request-breakdown-info-filter/>
    </v-container>
    <v-container v-if="component==='ATTENDANCE_FILTER'">
     <attendance-filter></attendance-filter>
    </v-container>
    <v-container v-if="component==='DATA_PURGE_FILTER'">
      <DataPurgeFilter />
    </v-container>
  </v-navigation-drawer>
</template>

<script>
  import { SET_DRAWER_STATE, SET_UNDO_TYPE } from '../store/mutation-types'
  import InterventionForm from './intervention/InterventionForm'
  import CloseInterventionForm from './intervention/CloseInterventionForm'
  import ExemptionForm from './exemption/ExemptionForm'
  import ChartFilterForm from './dashboard/ChartFilterForm'
  import { components } from '../store/modules/rightDrawer'
  import { mapActions, mapMutations, mapState } from 'vuex'
  import { SET_UNDO_STATE_ASYNC } from '../store/action-types'
  import { undoTypes } from '../store/modules/undo'
  import InteractionsFilter from './intervention/InteractionsFilter'
  import StudentFilterContainer from './filter/StudentFilterContainer'
  import Colors from '../mixins/Colors'
  import AlertFilterBody from './settings/alert/AlertFilterBody'
  import FeedRequestBreakdownInfoFilter from './monitor/FeedRequestBreakdownInfoFilter'
  import AttendanceFilter from './student/attendance/AttendanceFilter'
  import DataPurgeFilter from './data-purge/DataPurgeFilter'

  export default {
    components: {
      DataPurgeFilter,
      AttendanceFilter,
      AlertFilterBody,
      StudentFilterContainer,
      InterventionForm,
      CloseInterventionForm,
      InteractionsFilter,
      ExemptionForm,
      ChartFilterForm,
      FeedRequestBreakdownInfoFilter
    },
    name: 'right-drawer',
    mixins: [Colors],
    methods: {
      cancelClicked () {
        this.closeDrawer()
      },
      drawerClosed () {
        switch (this.component) {
          case (components.CREATE_INTERVENTION):
            if (!this.$store.state.intervention.interventionId) {
              this.setUndoType(undoTypes.INTERVENTION_CREATION)
              this.setUndoSnackbar(true)
            } else {
              this.setUndoType(undoTypes.INTERVENTION_EDIT)
              this.setUndoSnackbar(true)
            }
            break
          case (components.CREATE_EXEMPTION):
            if (!this.$store.state.exemptionModule.exemptionId) {
              this.setUndoType(undoTypes.EXEMPTION_CREATION)
              this.setUndoSnackbar(true)
            } else {
              this.setUndoType(undoTypes.EXEMPTION_EDIT)
              this.setUndoSnackbar(true)
            }
            break
          default:
            break
        }
      },
      closeDrawer () {
        this.drawer = false
      },
      resetForm (formName) {
        this.$nextTick(() => {
          let form = this.$refs[formName]
          form.resetForm()
        })
      },
      ...mapActions({
        setUndoSnackbar: SET_UNDO_STATE_ASYNC
      }),
      ...mapMutations({
        setUndoType: SET_UNDO_TYPE
      }),
      replaceValues (val) {
        return val ? `-${val.replace('_', '-').toLowerCase()}` : ''
      }
    },
    watch: {
      drawer (value) {
        if (value) {
          this.setUndoSnackbar(false)
        } else {
          this.drawerClosed()
        }

        if (this.component === components.CREATE_INTERVENTION) {
          this.$nextTick(() => {
            this.$refs.intForm.$refs.form.inputs.forEach((input) => {
              // Resets the status of validation whenever the drawer opens/closes
              input.shouldValidate = false
              input.isBooted = false
              input.hasFocused = false
            })
          })
        } else if (this.component === components.CLOSE_INTERVENTION) {
          this.resetForm('closeIntForm')
        } else if (this.component === components.CREATE_EXEMPTION) {
          this.resetForm('exemptionForm')
        }
      }
    },
    computed: {
      textColor () {
        return {color: this.getTextColor(this.$vuetify.theme.primary)}
      },
      ...mapState({
        title: state => state.rightDrawer.title,
        component: state => state.rightDrawer.component
      }),
      backButtonDisabled () {
        switch (this.component) {
          case (components.CREATE_INTERVENTION):
            return this.$store.state.intervention.sending
          case (components.CREATE_EXEMPTION):
            return this.$store.state.exemptionModule.sending
          default:
            return false
        }
      },
      drawer: {
        get () {
          return this.$store.state.rightDrawer.state
        },
        set (state) {
          this.$store.commit(SET_DRAWER_STATE, state)
        }
      },
      closeId () {
        return `close${this.replaceValues(this.component)}-drawer`
      }
    }
  }
</script>

<style scoped>
  .back-button {
    position: absolute;
    left: 0;
    top: 0;
  }

  .title {
    margin-left: 35px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
</style>



// WEBPACK FOOTER //
// src/components/RightDrawer.vue